import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Helmet } from "react-helmet-async";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import CiteSummary from "@/components/blog/CiteSummary";

const BLOG_POSTING_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": "CAP Inspection Readiness: A 90-Day Checklist",
  "url": "https://labcomplyx.com/blog/cap-inspection-readiness",
  "mainEntityOfPage": "https://labcomplyx.com/blog/cap-inspection-readiness",
  "datePublished": "2026-07-18",
  "dateModified": "2026-07-18",
  "author": { "@id": "https://labcomplyx.com/#founder" },
  "publisher": { "@id": "https://labcomplyx.com/#organization" },
};

const CHECKLIST = [
  "Review and update all SOPs — verify currency, signatures, and version history",
  "Confirm all personnel competency assessments are current and on file",
  "Audit QC logs for completeness and corrective action documentation",
  "Close all open CAPAs or document an accepted risk rationale",
  "Review proficiency testing enrollment and documentation",
  "Verify test menu matches documented procedures and personnel qualifications",
  "Confirm critical value notification logs are complete",
  "Review reference laboratory agreements and documentation",
  "Audit instrument maintenance and calibration records",
  "Conduct a mock inspection with a sample of records",
];

export default function CAPInspectionReadiness() {
  return (
    <>
      <SEO
        title="CAP Inspection Readiness: A 90-Day Checklist | LabComplyx Blog"
        description="A practical 90-day checklist for clinical labs preparing for CAP accreditation or renewal inspection. Close documentation gaps before inspectors arrive."
        canonical="https://labcomplyx.com/blog/cap-inspection-readiness"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BLOG_POSTING_SCHEMA)}</script>
      </Helmet>
      <div className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/blog" className="text-xs text-muted-foreground hover:text-primary transition-colors">← Back to Blog</Link>
          </div>
          <span className="inline-block text-xs font-medium text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">CAP · Inspection Prep</span>
          <h1 className="text-4xl font-bold text-foreground mb-5">CAP Inspection Readiness: A 90-Day Checklist</h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-10">
            CAP inspections are unannounced within a window. Labs that treat inspection readiness as a continuous discipline — not a scramble — consistently outperform those that don't. This checklist gives you a 90-day framework.
          </p>

          <CiteSummary
            heading="TL;DR — CAP Readiness Snapshot"
            items={[
              "CAP inspections are unannounced within a defined window — readiness must be continuous, not a pre-inspection scramble.",
              "Start remediation 90 days out: most documentation fixes (SOP updates, CAPA closure, competency records) take 4–8 weeks done properly.",
              "The most underestimated task is CAPA closure — open corrective actions without a documented effectiveness check take longer to close than labs expect.",
              "The 10-point checklist covers SOPs, competency assessments, QC logs, CAPAs, proficiency testing, test menu alignment, critical values, reference lab agreements, calibration records, and a mock inspection.",
            ]}
          />

          <div className="bg-card border border-border rounded-xl p-7 mb-10">
            <h2 className="text-lg font-bold text-foreground mb-5">90-Day CAP Inspection Readiness Checklist</h2>
            <ul className="space-y-3">
              {CHECKLIST.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="prose prose-sm max-w-none space-y-6 text-foreground">
            <section>
              <h2 className="text-xl font-bold mb-3">Why 90 days?</h2>
              <p className="text-muted-foreground leading-relaxed">Most documentation remediation — updating SOPs, closing open CAPAs, completing competency records — takes 4–8 weeks when done properly. Starting at 90 days gives you time to identify gaps, remediate them, and verify closure before the inspection window opens.</p>
            </section>
            <section>
              <h2 className="text-xl font-bold mb-3">Where most labs underestimate the work</h2>
              <p className="text-muted-foreground leading-relaxed">The most common underestimation is in CAPA closure. Labs often have open corrective actions with no documented effectiveness check — and closing them properly takes more time than expected. Start there first.</p>
            </section>
          </div>

          <div className="mt-12 p-7 bg-muted/30 border border-border rounded-xl">
            <h3 className="font-semibold text-foreground mb-3">Want a personalized inspection readiness review?</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">LabComplyx conducts free CAP and CLIA gap audits to identify the specific documentation gaps in your lab and build a prioritized remediation plan.</p>
            <Link to="/validation-intake"><Button className="gap-2">Book a Free Compliance Review <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
        </div>
      </div>
    </>
  );
}